import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  UserPlus, Search, PawPrint, QrCode, AlertTriangle, Bird, Star, Send,
  FlaskConical, KeyRound, CreditCard, Printer,
} from "lucide-react";

const STEPS = [
  { icon: UserPlus, title: "Crie sua conta", text: "Cadastro grátis com email ou Google. Marque que é tutor.", link: "/cadastro", cta: "Cadastrar grátis" },
  { icon: Search, title: "Explore o diretório", text: "Busque prestadores por categoria e bairro, abra um perfil, favorite e chame no WhatsApp.", link: "/prestadores", cta: "Explorar serviços" },
  { icon: PawPrint, title: "Cadastre seu pet", text: "Monte a carteira digital: vacinas, vermífugo, alergias e lembretes automáticos.", link: "/app", cta: "Minha conta" },
  { icon: QrCode, title: "Teste a loja", text: "Tags, coleiras personalizadas e anilhas para aves. Os pagamentos são simulados — nada é cobrado de verdade.", link: "/loja", cta: "Ver tags" },
  { icon: AlertTriangle, title: "Modo pet desaparecido", text: "Marque um pet como desaparecido e veja a rede comunitária em ação.", link: "/pets-perdidos", cta: "Ver pets perdidos" },
  { icon: Printer, title: "Cartaz Procura-se", text: "Gere e imprima o cartaz com QR code do seu pet em Minha conta → Tags & Pedidos.", link: "/app", cta: "Meus pets" },
  { icon: Bird, title: "Conheça a Anilha 2D", text: "Veja a nova identificação para aves e o perfil digital demo da calopsita Thor.", link: "/aves", cta: "Ver anilhas" },
];

export default function Testers() {
  const [form, setForm] = useState({ name: "", message: "", rating: 5 });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (form.message.trim().length < 3) return toast.error("Escreva seu feedback antes de enviar");
    setSending(true);
    try {
      await api.post("/feedback", form);
      setSent(true);
      toast.success("Feedback enviado! Muito obrigado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="testers-page">
      <section className="bg-brand-graphite text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 text-brand-mint text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <FlaskConical className="w-3.5 h-3.5" /> Versão de testes
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-heading leading-tight">
            Bem-vindo(a), <span className="text-brand-coral">testador!</span>
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto leading-relaxed">
            Você está ajudando a construir o PetMachine Hub. Siga o roteiro abaixo, explore à vontade
            e nos conte o que achou — cada feedback vira melhoria na plataforma.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-brand-mustardLight border border-brand-mustard/40 rounded-2xl p-6 mb-10" data-testid="demo-accounts-box">
          <h2 className="font-heading font-semibold text-lg text-brand-graphite flex items-center gap-2 mb-3">
            <KeyRound className="w-5 h-5 text-brand-mustardDark" /> Contas demo (opcional)
          </h2>
          <p className="text-sm text-muted-foreground mb-3">Prefere pular o cadastro? Use uma conta pronta:</p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-white rounded-xl p-4 border">
              <p className="font-semibold text-brand-graphite">Tutor demo</p>
              <p className="text-xs font-mono mt-1">tutor.demo@petmachinehub.com.br</p>
              <p className="text-xs font-mono">Demo@12345</p>
              <p className="text-xs text-muted-foreground mt-1">Já tem pets, Club ativo e tags com QR</p>
            </div>
            <div className="bg-white rounded-xl p-4 border">
              <p className="font-semibold text-brand-graphite">Prestador demo</p>
              <p className="text-xs font-mono mt-1">prestador.demo@petmachinehub.com.br</p>
              <p className="text-xs font-mono">Demo@12345</p>
              <p className="text-xs text-muted-foreground mt-1">Painel de prestador com leads e estatísticas</p>
            </div>
          </div>
          <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
            <p className="flex items-center gap-2"><QrCode className="w-3.5 h-3.5 text-brand-coral" /> Página pública de uma tag: <Link to="/tag/PMH-LUNA2026" className="text-brand-coral font-semibold" data-testid="demo-tag-link">/tag/PMH-LUNA2026</Link></p>
            <p className="flex items-center gap-2"><Bird className="w-3.5 h-3.5 text-brand-mint" /> Perfil digital de ave (anilha): <Link to="/anilha/PMH-000001" className="text-brand-coral font-semibold" data-testid="demo-anilha-link">/anilha/PMH-000001</Link></p>
          </div>
        </div>

        <div className="bg-brand-mintLight border border-brand-mint/30 rounded-2xl px-5 py-4 mb-10 flex items-start gap-3" data-testid="mock-payment-note">
          <CreditCard className="w-5 h-5 text-brand-mintDark shrink-0 mt-0.5" />
          <p className="text-sm text-brand-graphite">
            <strong>Pagamentos são simulados nesta fase.</strong> Pode "comprar" tags, coleiras, anilhas e assinar
            planos à vontade — nenhum valor é cobrado. A integração real com Mercado Pago entra em breve.
          </p>
        </div>

        <h2 className="font-heading font-bold text-2xl text-brand-graphite mb-6">Roteiro de testes</h2>
        <div className="space-y-4" data-testid="test-roadmap">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border p-5 flex items-start gap-4 card-hover">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${i % 2 === 0 ? "bg-brand-coralLight text-brand-coral" : "bg-brand-mintLight text-brand-mintDark"}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-heading font-semibold text-brand-graphite">{s.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.text}</p>
              </div>
              <Link to={s.link} data-testid={`roadmap-link-${i}`}>
                <Button variant="outline" size="sm" className="rounded-full shrink-0 border-brand-coral/30 text-brand-coral hover:bg-brand-coralLight">{s.cta}</Button>
              </Link>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl border-2 border-brand-coral/20 p-8 mt-12" data-testid="feedback-form">
          <h2 className="font-heading font-bold text-2xl text-brand-graphite text-center">Como foi sua experiência?</h2>
          <p className="text-sm text-muted-foreground text-center mt-2 mb-6">Achou algum erro? Faltou algo? Conta pra gente!</p>
          {sent ? (
            <div className="text-center py-8" data-testid="feedback-success">
              <Star className="w-10 h-10 fill-brand-mustard text-brand-mustard mx-auto mb-3" />
              <p className="font-heading font-semibold text-lg text-brand-graphite">Feedback recebido!</p>
              <p className="text-sm text-muted-foreground mt-1">Obrigado por ajudar o PetMachine Hub a ficar melhor.</p>
            </div>
          ) : (
            <div className="space-y-4 max-w-lg mx-auto">
              <div>
                <Label>Seu nome (opcional)</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Como podemos te chamar?" className="mt-1" data-testid="feedback-name-input" />
              </div>
              <div>
                <Label>Nota geral</Label>
                <div className="flex gap-1.5 mt-2" data-testid="feedback-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`feedback-star-${n}`}>
                      <Star className={`w-8 h-8 transition-colors ${n <= form.rating ? "fill-brand-mustard text-brand-mustard" : "text-gray-300 hover:text-brand-mustard"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Seu feedback</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="O que você achou? Encontrou algum problema? O que sentiu falta?" rows={4} className="mt-1" data-testid="feedback-message-input" />
              </div>
              <Button onClick={submit} disabled={sending} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full h-11" data-testid="feedback-submit-btn">
                <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar feedback"}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
